import type { Locale } from '@tuic/i18n';
import { CacheTag } from '../../utils';
import { remember, serializeDataToCacheKey } from '@tuic/cache';
import { Pcl } from '../../index';
import { logger } from '@tuic/logger';

/**
 * Fetches the cabin information from the PCL API.
 */
export const getTripCabinTypesAndCategories = async (
  tripCode: string,
  locale: Locale,
): Promise<Pcl.CabinInformation[]> => {
  let { data, error } = await Pcl.getTripsByTripCodeCabinTypesAndCategories({
    path: { tripCode },
    query: { locale },
  });

  if (error) {
    logger.error(
      `Could not fetch cabin information. Trip: ${tripCode}, Locale: ${locale}`,
    );
    logger.error(error);

    return [];
  }

  return data ?? [];
};

export const cachedGetTripCabinTypesAndCategories = (
  tripCode: string,
  locale: Locale,
): Promise<Pcl.CabinInformation[]> =>
  remember(
    serializeDataToCacheKey([
      CacheTag.pcl,
      'getTripCabinTypesAndCategories',
      locale,
      tripCode,
    ]),
    () => getTripCabinTypesAndCategories(tripCode, locale),
    {
      tags: [
        CacheTag.pcl,
        CacheTag.tripResource,
        CacheTag.getTripCabinTypesAndCategories,
      ],
      ttl: 600,
    },
  );

/**
 * Fetches the cabin information from the PCL API for a specific ship.
 */
export const getShipCabinTypesAndCategories = async (
  shipCode: string,
  locale: Locale,
): Promise<Pcl.CabinInformation[]> => {
  let { data, error } = await Pcl.getShipsByShipCodeCabinTypesAndCategories({
    path: { shipCode },
    query: { locale },
  });

  if (error) {
    logger.error(
      `Could not fetch cabin information. Ship: ${shipCode}, Locale: ${locale}`,
    );
    logger.error(error);

    return [];
  }

  return data ?? [];
};

export const cachedGetShipCabinTypesAndCategories = (
  shipCode: string,
  locale: Locale,
): Promise<Pcl.CabinInformation[]> =>
  remember(
    serializeDataToCacheKey([
      CacheTag.pcl,
      'getShipCabinTypesAndCategories',
      locale,
      shipCode,
    ]),
    () => getShipCabinTypesAndCategories(shipCode, locale),
    {
      tags: [
        CacheTag.pcl,
        CacheTag.shipResource,
        CacheTag.getShipCabinTypesAndCategories,
      ],
      ttl: 600,
    },
  );

/**
 * Fetches the cabin information from the PCL API for a specific cabin category
 */
export const getTripCabinTypesAndCategoriesByCabinCategoryCode = async (
  tripCode: string,
  cabinCategoryCode: string,
  locale: Locale,
): Promise<Pcl.CabinInformation> => {
  let { data, error } =
    await Pcl.getTripsByTripCodeCabinTypesAndCategoriesByCabinCategoryCode({
      path: { tripCode, cabinCategoryCode },
      query: { locale },
    });

  if (error) {
    logger.error(
      `Could not fetch cabin information for cabin category. Trip: ${tripCode}, cabin category code: ${cabinCategoryCode}, Locale: ${locale}`,
    );
    logger.error(error);

    return {};
  }

  return data ?? {};
};

export const cachedGetTripCabinTypesAndCategoriesByCabinCategoryCode = (
  tripCode: string,
  cabinCategoryCode: string,
  locale: Locale,
): Promise<Pcl.CabinInformation> =>
  remember(
    serializeDataToCacheKey([
      CacheTag.pcl,
      'getTripCabinTypesAndCategoriesByCabinCategoryCode',
      locale,
      tripCode,
    ]),
    () =>
      getTripCabinTypesAndCategoriesByCabinCategoryCode(
        tripCode,
        cabinCategoryCode,
        locale,
      ),
    {
      tags: [
        CacheTag.pcl,
        CacheTag.tripResource,
        CacheTag.getTripCabinTypesAndCategoriesByCabinCategoryCode,
      ],
      ttl: 600,
    },
  );

/**
 * Fetches all cabin types from the PCL API.
 */
export const getCabinCategoryTypes = async (
  locale: Locale,
): Promise<Pcl.CabinCategoryTypeInfo[]> => {
  let { data, error } = await Pcl.getCabinCategoryTypes({
    query: { locale },
  });

  if (error) {
    logger.error(`Could not fetch cabin information. Locale: ${locale}`);
    logger.error(error);

    return [];
  }

  return data ?? [];
};

export const cachedGetCabinCategoryTypes = (
  locale: Locale,
): Promise<Pcl.CabinCategoryTypeInfo[]> =>
  remember(
    serializeDataToCacheKey([CacheTag.pcl, 'getCabinCategoryTypes', locale]),
    () => getCabinCategoryTypes(locale),
    {
      tags: [
        CacheTag.pcl,
        CacheTag.cabinCategoryTypeResource,
        CacheTag.getCabinCategoryTypes,
      ],
      ttl: 600,
    },
  );
